import React, { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import LanIcon from '@mui/icons-material/Lan';
import LanguageIcon from '@mui/icons-material/Language';
import PublicIcon from '@mui/icons-material/Public';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import {
  FirewallAdresseAufgeloest,
  FirewallInterfaceSeite,
  FirewallRegel,
  FirewallRisiko,
  FirewallServiceInfo,
  FirewallStatistik,
  FirewallTeamMitglied,
} from '../../services/firewallService';
import {
  addressView,
  byteHistogram,
  FARBE_FILL,
  FARBE_LABEL,
  FARBE_TEXT,
  FirewallFarbe,
  colorsDisagree,
  findSimilarRules,
  joinList,
  percentileText,
  ratingByMember,
  ratingState,
  ratingStateLabel,
  RATING_UI_COLOR,
} from '../../utils/firewallReview';
import { MONO, SwipePalette, useSwipePalette } from './firewallSwipeTheme';

export const ACCEPT_COLOR = '#22c55e';
export const DENY_COLOR = '#ef4444';
const WARN_BG = 'rgba(240, 180, 41, 0.12)';
const WARN_BORDER = 'rgba(240, 180, 41, 0.6)';
const WARN_TEXT_LIGHT = '#8a5a00';

const GRUND_LABEL: Record<string, string> = {
  vip: 'VIP – nicht in der Adressdatei',
  gruppe: 'Adressgruppe – nicht in der Adressdatei',
  internet_service: 'Internet-Service – kein Adressobjekt',
  range: 'Inline-Bereich – kein Adressobjekt',
  keine_adressdatei: 'Keine Adressdatei für diesen VDOM',
  keine_interfacedatei: 'Keine Interface-Datei für diesen VDOM',
  implicit: 'Implizite Regel',
  unbekannt: 'Nicht in der Adressdatei',
};

const IFACE_TYP_KURZ: Record<string, string> = {
  'Physical Interface': 'physisch',
  Zone: 'Zone',
  'VDOM Link Interface': 'VDOM-Link',
  'VDOM Link': 'VDOM-Link',
  'Loopback Interface': 'Loopback',
  'Tunnel Interface': 'Tunnel',
  'Aggregate Interface': 'Aggregat',
  'VLAN Interface': 'VLAN',
};

export function isAccept(action: string | null | undefined): boolean {
  return (action ?? '').trim().toUpperCase() === 'ACCEPT';
}

export function actionColor(action: string | null | undefined): string {
  return isAccept(action) ? ACCEPT_COLOR : DENY_COLOR;
}

function typIcon(typ: string | null | undefined, resolved: boolean, color: string): React.ReactNode {
  if (!resolved) return <HelpOutlineIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
  switch (typ) {
    case 'FQDN':
      return <LanguageIcon fontSize="small" sx={{ color }} />;
    case 'Geography':
      return <PublicIcon fontSize="small" sx={{ color }} />;
    case 'IP Range':
      return <LinearScaleIcon fontSize="small" sx={{ color }} />;
    default:
      return <LanIcon fontSize="small" sx={{ color }} />;
  }
}

// ── Prüfhinweise ──────────────────────────────────────────────────────────

const RISK_TITLE: Record<string, (risk: FirewallRisiko) => string> = {
  dienst_name: () => 'Name und Inhalt weichen ab',
  wildcard_service: (risk) => `Wildcard-Service: ${risk.grund}`,
  ohne_inspection: () => 'Keine Inspection bei ACCEPT',
  kein_kommentar: () => 'Kein Policy-Kommentar',
  deaktiviert: () => 'Regel deaktiviert',
  any: () => 'Any-Regel',
  log: () => 'Logging nicht „All“',
  nie_genutzt: () => 'Nie genutzt',
  schedule: () => 'Zeitplan aktiv',
  nat: () => 'NAT aktiv',
  risikoport: (risk) => risk.label,
};

const RISK_EXPLANATION: Record<string, (risk: FirewallRisiko) => string> = {
  dienst_name: (risk) => risk.grund,
  wildcard_service: (risk) => (/icmp/i.test(risk.grund) && !/(^|,\s*)all(,|$)/i.test(risk.grund)
    ? 'Erlaubt alle ICMP-Typen und -Codes.'
    : 'Erlaubt alle Dienste und Ports.'),
  ohne_inspection: () => 'Die Regel hat keine Inspection-Regeln.',
  kein_kommentar: () => 'In der Regel ist kein Kommentar hinterlegt.',
  deaktiviert: () => 'Die Policy ist auf der Firewall deaktiviert.',
  any: (risk) => `${risk.grund} – ohne Einschränkung.`,
  log: (risk) => `Log-Einstellung „${risk.grund}“ statt „All“.`,
  nie_genutzt: () => 'Die Regel hatte bisher keinen Verkehr (0 Byte).',
  schedule: (risk) => `Gilt nur nach Zeitplan „${risk.grund}“.`,
  nat: (risk) => (risk.grund === 'ohne IP-Pool' ? 'NAT ohne IP-Pool.' : `NAT mit IP-Pool ${risk.grund}.`),
  risikoport: (risk) => risk.grund,
};

export function riskTitle(risk: FirewallRisiko): string {
  return (RISK_TITLE[risk.code] ?? (() => risk.label))(risk);
}

export function riskExplanation(risk: FirewallRisiko): string {
  return (RISK_EXPLANATION[risk.code] ?? (() => risk.grund))(risk);
}

/** Einklappbares Feld „N Prüfhinweise“ mit einer Kachel je Risikofaktor; ohne Treffer nicht gerendert. */
export const RiskStrip: React.FC<{ risiken: FirewallRisiko[]; compact?: boolean; defaultOpen?: boolean }> = ({
  risiken,
  compact,
  defaultOpen = true,
}) => {
  const palette = useSwipePalette();
  const [open, setOpen] = useState(defaultOpen);
  if (risiken.length === 0) return null;
  const hasRed = risiken.some((item) => item.stufe === 'rot');
  const tone = hasRed ? palette.red : palette.amber;
  const toneBg = hasRed ? palette.redBg : palette.amberBg;
  const toneBorder = hasRed ? palette.redBorder : palette.amberBorder;
  return (
    <Box
      data-testid="firewall-risk-strip"
      sx={{
        mt: compact ? 1.25 : 1.75,
        borderRadius: 2,
        border: `1px solid ${toneBorder}`,
        bgcolor: toneBg,
        overflow: 'hidden',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        data-testid="firewall-risk-toggle"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((value) => !value);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.9,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <WarningAmberIcon sx={{ fontSize: 18, color: tone }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: tone, flex: 1 }}>
          {risiken.length} {risiken.length === 1 ? 'Prüfhinweis' : 'Prüfhinweise'}
        </Typography>
        {open ? <ExpandLessIcon sx={{ color: tone }} /> : <ExpandMoreIcon sx={{ color: tone }} />}
      </Box>
      <Collapse in={open}>
        <Box
          role="list"
          aria-label="Risikofaktoren"
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 180 : 200}px, 1fr))`,
            gap: 1,
            px: 1.5,
            pb: 1.5,
          }}
        >
          {risiken.map((item) => {
            const rot = item.stufe === 'rot';
            const itemTone = rot ? palette.red : palette.amber;
            return (
              <Box
                key={`${item.code}-${item.label}`}
                role="listitem"
                data-testid={rot ? 'firewall-risk-any' : 'firewall-risk-factor'}
                data-risk-code={item.code}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  border: `1px solid ${rot ? palette.redBorder : palette.amberBorder}`,
                  bgcolor: palette.isDark ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.6)',
                  minWidth: 0,
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 18, color: itemTone, mt: 0.1, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: itemTone, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                    {riskTitle(item)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: palette.muted, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                    {riskExplanation(item)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

/** Kompakte Marker für Listenzeilen: rotes „Any“ plus Anzahl der Risikofaktoren. */
export const RiskMarkers: React.FC<{ rule: Pick<FirewallRegel, 'risiken' | 'ist_any'> }> = ({ rule }) => {
  const risiken = rule.risiken ?? [];
  if (risiken.length === 0) return null;
  const anyRule = rule.ist_any || risiken.some((item) => item.code === 'any');
  return (
    <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }} data-testid="firewall-row-risks">
      {anyRule && (
        <Tooltip title={risiken.find((item) => item.code === 'any')?.grund ?? 'Any-Regel'} arrow>
          <Chip
            size="small"
            label="Any"
            data-testid="firewall-row-any"
            sx={{
              height: 20,
              fontWeight: 800,
              fontSize: '0.7rem',
              color: '#fff',
              bgcolor: DENY_COLOR,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Tooltip>
      )}
      <Tooltip title={risiken.map((item) => `${item.label}: ${item.grund}`).join(' · ')} arrow>
        <Box
          component="span"
          data-testid="firewall-row-risk-count"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            fontSize: '0.72rem',
            fontWeight: 600,
            color: '#b26a00',
            px: 0.5,
            borderRadius: 1,
            bgcolor: WARN_BG,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 13 }} />
          {risiken.length}
        </Box>
      </Tooltip>
    </Box>
  );
};

// ── Adress-Kachel ─────────────────────────────────────────────────────────

export const NoIpStatus: React.FC<{ hinweis: string | null; grund?: string | null }> = ({ hinweis, grund }) => {
  const palette = useSwipePalette();
  const text = palette.isDark ? palette.amber : WARN_TEXT_LIGHT;
  return (
    <Tooltip title={GRUND_LABEL[grund ?? 'unbekannt'] ?? 'Nicht aufgelöst'} arrow>
      <Box data-testid="firewall-no-ip" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label="Keine IP bekannt"
          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: WARN_BG, border: `1px solid ${WARN_BORDER}`, color: text }}
        />
        {hinweis && (
          <Typography component="span" variant="caption" data-testid="firewall-no-ip-reason" sx={{ color: text, fontWeight: 700 }}>
            {hinweis}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export const IpMismatchNote: React.FC<{ aufgeloest: string; imNamen: string }> = ({ aufgeloest, imNamen }) => {
  const palette = useSwipePalette();
  const text = palette.isDark ? palette.amber : WARN_TEXT_LIGHT;
  return (
    <Box
      data-testid="firewall-ip-mismatch"
      sx={{
        mt: 0.75,
        px: 1,
        py: 0.6,
        borderRadius: 1,
        bgcolor: WARN_BG,
        border: `1px solid ${WARN_BORDER}`,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        alignItems: 'baseline',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 800, color: text, minWidth: 0, overflowWrap: 'anywhere' }}>
        IP im Namen weicht ab
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: MONO, fontWeight: 700, color: text, minWidth: 0, overflowWrap: 'anywhere' }}>
        aufgelöst {aufgeloest}
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: MONO, fontWeight: 700, color: text, minWidth: 0, overflowWrap: 'anywhere' }}>
        im Namen {imNamen}
      </Typography>
    </Box>
  );
};

const AddressEntry: React.FC<{ item: FirewallAdresseAufgeloest; compact?: boolean; palette: SwipePalette }> = ({
  item,
  compact,
  palette,
}) => {
  const view = addressView(item);
  const extra = item.aufgeloest ? item.werte.slice(1) : [];
  return (
    <Box
      data-testid={view.keineIp ? 'firewall-address-unresolved' : 'firewall-address-resolved'}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.5, minWidth: 0 }}
    >
      <Box sx={{ pt: compact ? 0.2 : 0.4 }}>{typIcon(item.typ, !view.keineIp, palette.green)}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {item.ist_all && view.keineIp ? (
          <Chip
            data-testid="firewall-address-all"
            size={compact ? 'small' : 'medium'}
            label={view.headline}
            sx={{ bgcolor: DENY_COLOR, color: '#fff', fontWeight: 700, letterSpacing: 0.5 }}
          />
        ) : (
          <Typography
            data-testid="firewall-address-headline"
            sx={{
              fontWeight: 700,
              fontSize: compact ? '0.98rem' : '1.2rem',
              fontFamily: view.monospace ? MONO : undefined,
              wordBreak: 'break-all',
              lineHeight: 1.25,
              color: palette.text,
            }}
          >
            {view.headline}
          </Typography>
        )}
        {extra.length > 0 && (
          <Typography variant="caption" sx={{ display: 'block', fontFamily: MONO, wordBreak: 'break-all', color: palette.muted }}>
            {extra.join(', ')}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
          {view.secondary && (
            <Typography variant="caption" data-testid="firewall-address-caption" sx={{ wordBreak: 'break-all', color: palette.muted }}>
              {view.secondary}
            </Typography>
          )}
          {item.mapped_from && (
            <Typography variant="caption" data-testid="firewall-vip-from" sx={{ wordBreak: 'break-all', color: palette.muted }}>
              {`von ${item.mapped_from}`}
            </Typography>
          )}
          {item.ist_all && !view.keineIp && (
            <Chip
              data-testid="firewall-address-all"
              size="small"
              label="all"
              sx={{ height: 18, fontSize: '0.65rem', bgcolor: DENY_COLOR, color: '#fff', fontWeight: 700 }}
            />
          )}
          {!view.keineIp && item.typ && (
            <Chip
              data-testid="firewall-address-type"
              size="small"
              variant="outlined"
              label={item.typ}
              sx={{ height: 18, fontSize: '0.65rem', color: palette.muted, borderColor: palette.tileBorder }}
            />
          )}
          {!view.keineIp && item.interface && !hidesAnyInterfaceChip(item) && (
            <Chip
              data-testid="firewall-address-interface"
              size="small"
              variant="outlined"
              label={`an ${item.interface}`}
              sx={{ height: 18, fontSize: '0.65rem', color: palette.muted, borderColor: palette.tileBorder }}
            />
          )}
          {view.keineIp && <NoIpStatus hinweis={view.hinweis} grund={item.grund} />}
        </Box>
        {view.widerspruch && (
          <IpMismatchNote aufgeloest={view.widerspruch.aufgeloest} imNamen={view.widerspruch.im_namen} />
        )}
      </Box>
    </Box>
  );
};

const InterfaceRow: React.FC<{ side: FirewallInterfaceSeite | null | undefined; align: 'left' | 'right'; palette: SwipePalette }> = ({
  side,
  align,
  palette,
}) => {
  if (!side) return null;
  const typ = side.typ ? IFACE_TYP_KURZ[side.typ] ?? side.typ : null;
  const detail = side.aufgeloest
    ? [typ, side.ip ?? (side.members.length > 0 ? side.members.join(', ') : null)].filter(Boolean).join(' · ')
    : null;
  const missing = !side.aufgeloest && side.grund !== 'implicit';
  const reason = GRUND_LABEL[side.grund ?? 'unbekannt'] ?? 'Nicht aufgelöst';
  const warnText = palette.isDark ? palette.amber : WARN_TEXT_LIGHT;
  return (
    <Box
      data-testid={`firewall-interface-${align}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        mt: 1.25,
        pt: 1,
        borderTop: `1px solid ${palette.tileBorder}`,
        minWidth: 0,
      }}
    >
      <DeveloperBoardIcon sx={{ fontSize: 18, color: palette.muted }} />
      <Typography variant="body2" sx={{ color: palette.text, minWidth: 0, overflowWrap: 'anywhere' }}>
        <Box component="span" sx={{ color: palette.muted }}>Interface: </Box>
        <Box component="span" sx={{ fontWeight: 700 }}>{side.name}</Box>
      </Typography>
      {detail && (
        <Typography variant="caption" sx={{ color: palette.muted, fontFamily: side.ip ? MONO : undefined }}>
          {detail}
        </Typography>
      )}
      {!side.aufgeloest && side.grund === 'implicit' && (
        <Typography variant="caption" sx={{ color: palette.muted }}>implizit</Typography>
      )}
      {missing && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: warnText }} />
          <Typography variant="caption" sx={{ color: warnText, fontWeight: 600 }}>
            {side.grund === 'keine_interfacedatei' ? 'Interface-Datei fehlt' : 'Nicht aufgelöst'}
          </Typography>
          <Tooltip title={reason} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: palette.muted, cursor: 'help' }} />
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

const AddressBox: React.FC<{
  title: string;
  items: FirewallAdresseAufgeloest[];
  side: FirewallInterfaceSeite | null | undefined;
  align: 'left' | 'right';
  compact?: boolean;
  testId: string;
  palette: SwipePalette;
}> = ({ title, items, side, align, compact, testId, palette }) => {
  const [open, setOpen] = useState(false);
  const limit = 4;
  const visible = open || items.length <= limit ? items : items.slice(0, limit);
  const hidden = items.length - visible.length;
  return (
    <Box
      data-testid={testId}
      sx={{
        p: compact ? 1.25 : 1.75,
        flex: 1,
        minWidth: 0,
        borderRadius: 2.5,
        bgcolor: palette.tile,
        border: `1px solid ${palette.tileBorder}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography sx={{ color: palette.muted, fontSize: '0.68rem', letterSpacing: 1.2, fontWeight: 700, textTransform: 'uppercase' }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1 }}>
        {items.length === 0 && <Typography variant="body2" sx={{ color: palette.muted }}>–</Typography>}
        {visible.map((item, index) => (
          <AddressEntry key={`${item.name}-${index}`} item={item} compact={compact} palette={palette} />
        ))}
        {hidden > 0 && (
          <Button size="small" onClick={() => setOpen(true)} sx={{ mt: 0.5, px: 1 }}>
            +{hidden} weitere
          </Button>
        )}
        {open && items.length > limit && (
          <Button size="small" onClick={() => setOpen(false)} sx={{ mt: 0.5, px: 1 }}>
            Weniger
          </Button>
        )}
      </Box>
      <InterfaceRow side={side} align={align} palette={palette} />
    </Box>
  );
};

// ── Pfeil ─────────────────────────────────────────────────────────────────

const ConnectionArrow: React.FC<{ rule: FirewallRegel; compact?: boolean }> = ({ rule, compact }) => {
  const accept = isAccept(rule.action);
  const color = actionColor(rule.action);
  const resolvedPools = (rule.ip_pool_aufgeloest ?? []).filter((item) => item.aufgeloest && item.wert);
  const pool = rule.ip_pool.filter((item) => String(item).trim().length > 0);
  const poolLabel = resolvedPools.length > 0
    ? resolvedPools.map((item) => item.wert).join(', ')
    : pool.join(', ');
  return (
    <Box
      data-testid="firewall-connection-arrow"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        width: compact ? 110 : 140,
        flexShrink: 0,
        px: 0.5,
        gap: 0.5,
        alignSelf: 'stretch',
      }}
    >
      {rule.analyse.nat_aktiv && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title={pool.length > 0 ? `IP-Pool: ${pool.join(', ')}` : 'NAT ohne IP Pool'} arrow>
            <Chip
              data-testid="firewall-nat-badge"
              size="small"
              icon={<SwapHorizIcon />}
              label={poolLabel ? `NAT · ${poolLabel}` : 'NAT'}
              color="secondary"
              sx={{ fontWeight: 700, maxWidth: '100%', height: 22, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
          </Tooltip>
          {resolvedPools.length > 0 && (
            <Typography
              variant="caption"
              data-testid="firewall-pool-name"
              sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', lineHeight: 1.2, overflowWrap: 'anywhere' }}
            >
              {resolvedPools.map((item) => item.name).join(', ')}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ position: 'relative', height: 34, display: 'flex', alignItems: 'center' }}>
        <svg width="100%" height="34" viewBox="0 0 140 34" preserveAspectRatio="none" style={{ overflow: 'visible' }}>
          <line
            x1="2"
            y1="17"
            x2="126"
            y2="17"
            stroke={color}
            strokeWidth={accept ? 3 : 2.5}
            strokeDasharray={accept ? undefined : '7 5'}
            strokeLinecap="round"
          />
          <polygon points="124,9 138,17 124,25" fill={color} />
        </svg>
        <Chip
          size="small"
          label={rule.action || '–'}
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: color,
            color: accept ? '#0b2e16' : '#fff',
            fontWeight: 800,
            letterSpacing: 0.6,
            height: 24,
            fontSize: '0.72rem',
          }}
        />
      </Box>
    </Box>
  );
};

// ── Dienste & Ports ───────────────────────────────────────────────────────

/** Chip für Listenzeilen auf der Audit-Seite. */
export const ServiceChip: React.FC<{ service: FirewallServiceInfo; compact?: boolean; showCount?: boolean }> = ({
  service,
  compact,
  showCount = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const origin = service.objekt || service.raw;
  const ports = service.bereiche.length > 0 ? service.bereiche.join(', ') : '';
  const label = ports && ports.toLowerCase() !== origin.toLowerCase()
    ? ports
    : service.auffaellig ? service.raw : service.label;
  const tooltipParts = [
    origin && origin !== label ? `Dienst: ${origin}${service.gruppe ? ` · ${service.gruppe}` : ''}` : null,
    service.bereiche.length > 1 ? `Bereiche: ${service.bereiche.join(', ')}` : null,
    service.quelle === 'iana' ? 'Name aus IANA-Registry' : service.quelle === 'fortinet' ? 'Fortinet-Standarddienst' : 'Kein Klarname bekannt',
    service.risiko ? `Risikoport: ${service.risiko_grund ?? ''}` : null,
    showCount && service.weitere_accept > 0
      ? `${service.weitere_accept} weitere ACCEPT-Regel${service.weitere_accept === 1 ? '' : 'n'} im Audit mit diesem Service`
      : null,
  ].filter(Boolean);
  const bg = service.auffaellig
    ? DENY_COLOR
    : service.risiko
      ? WARN_BG
      : isDark
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.06)';
  return (
    <Tooltip title={tooltipParts.join(' · ')} arrow>
      <Chip
        data-testid={service.risiko ? 'firewall-service-risk' : 'firewall-service'}
        size={compact ? 'small' : 'medium'}
        icon={service.risiko ? <WarningAmberIcon sx={{ color: '#b26a00 !important' }} /> : undefined}
        label={(
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700 }}>
              {label}
            </Box>
            {showCount && service.weitere_accept > 0 && (
              <Box
                component="span"
                sx={{
                  fontSize: '0.68rem',
                  px: 0.6,
                  borderRadius: 1,
                  bgcolor: service.auffaellig ? 'rgba(255,255,255,0.3)' : isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.08)',
                }}
              >
                +{service.weitere_accept}
              </Box>
            )}
          </Box>
        )}
        sx={{
          bgcolor: bg,
          color: service.auffaellig ? '#fff' : 'text.primary',
          border: service.risiko ? `1px solid ${WARN_BORDER}` : undefined,
          height: compact ? 24 : 32,
          '& .MuiChip-label': { px: compact ? 1 : 1.5 },
        }}
      />
    </Tooltip>
  );
};

/** Aufgelöste VIPs tragen in Fortinet oft Interface „any“. Die Blase sagt nichts aus. */
function hidesAnyInterfaceChip(item: FirewallAdresseAufgeloest): boolean {
  const typ = (item.typ ?? '').trim().toLowerCase();
  const iface = (item.interface ?? '').trim().toLowerCase();
  return typ.startsWith('virtual ip') && iface === 'any';
}

function portShareText(port: string, count: number): string {
  if (count <= 0) return `Keine weitere ACCEPT-Regel im Audit mit ${port}`;
  return `${count} weitere ACCEPT-Regel${count === 1 ? '' : 'n'} im Audit mit ${port}`;
}

const ServiceMismatchNote: React.FC<{ behauptet: string; geoeffnet: string }> = ({ behauptet, geoeffnet }) => {
  const palette = useSwipePalette();
  const textColor = palette.isDark ? palette.amber : WARN_TEXT_LIGHT;
  return (
    <Box
      data-testid="firewall-service-mismatch"
      sx={{
        mt: 0.75,
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        bgcolor: WARN_BG,
        border: `1px solid ${WARN_BORDER}`,
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <WarningAmberIcon sx={{ fontSize: 18, color: textColor, mt: 0.1, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: textColor, lineHeight: 1.3 }}>
          Name und Inhalt weichen ab
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: palette.muted, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
          {`Name nennt ${behauptet}, geöffnet ist ${geoeffnet}.`}
        </Typography>
      </Box>
    </Box>
  );
};

const ServiceBlock: React.FC<{ service: FirewallServiceInfo; palette: SwipePalette }> = ({ service, palette }) => {
  const ports = service.bereiche.length > 0 ? service.bereiche : [service.auffaellig ? service.raw : service.label];
  const origin = service.objekt || service.raw;
  const caption = service.gruppe ? `${origin} · ${service.gruppe}` : origin;
  const showCaption = Boolean(caption) && ports.some((port) => port.toLowerCase() !== caption.toLowerCase());
  const warnText = palette.isDark ? palette.amber : WARN_TEXT_LIGHT;
  return (
    <Box
      data-testid={service.auffaellig ? 'firewall-service-row-wildcard' : service.risiko ? 'firewall-service-row-risk' : 'firewall-service-row'}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1.5,
        bgcolor: service.auffaellig ? palette.redBg : 'transparent',
        borderLeft: service.auffaellig ? `3px solid ${palette.red}` : '3px solid transparent',
      }}
    >
      {ports.map((port, index) => (
        <Box
          key={`${port}-${index}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
        >
          <Typography
            data-testid="firewall-service-port"
            sx={{ flex: 1, minWidth: 0, fontFamily: MONO, fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3, color: palette.text, overflowWrap: 'anywhere' }}
          >
            {port}
          </Typography>
          <Tooltip title={portShareText(port, service.weitere_accept)} arrow>
            <Box
              component="span"
              data-testid="firewall-port-info"
              data-port={port}
              aria-label={`Hinweise zu ${port}`}
              sx={{ display: 'inline-flex', flexShrink: 0, cursor: 'help', color: palette.muted }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        </Box>
      ))}
      {showCaption && (
        <Typography
          variant="caption"
          data-testid="firewall-service-caption"
          sx={{ display: 'block', color: palette.muted, lineHeight: 1.3, overflowWrap: 'anywhere', pr: 3 }}
        >
          {caption}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.4 }}>
        {service.auffaellig && (
          <Chip size="small" label="Wildcard" data-testid="firewall-service-wildcard" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, color: palette.red, border: `1px solid ${palette.redBorder}`, bgcolor: 'transparent' }} />
        )}
        {service.risiko && (
          <Chip
            size="small"
            icon={<WarningAmberIcon sx={{ fontSize: 13, color: `${warnText} !important` }} />}
            label="Risikoport"
            data-testid="firewall-service-risk"
            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, color: warnText, border: `1px solid ${WARN_BORDER}`, bgcolor: WARN_BG }}
          />
        )}
      </Box>
      {service.name_widerspruch && (
        <ServiceMismatchNote behauptet={service.name_widerspruch.behauptet} geoeffnet={service.name_widerspruch.geoeffnet} />
      )}
    </Box>
  );
};

const ServicesTable: React.FC<{ services: FirewallServiceInfo[]; palette: SwipePalette; compact?: boolean }> = ({
  services,
  palette,
  compact,
}) => (
  <Box
    data-testid="firewall-services"
    sx={{
      borderRadius: 2.5,
      bgcolor: palette.tile,
      border: `1px solid ${palette.tileBorder}`,
      p: compact ? 1.25 : 1.5,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Typography sx={{ fontWeight: 700, color: palette.text, fontSize: '0.95rem', mb: 0.5 }}>Dienste &amp; Ports</Typography>
    {services.length === 0 && <Typography variant="body2" sx={{ color: palette.muted, px: 1 }}>–</Typography>}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {services.map((service, index) => (
        <ServiceBlock key={`${service.raw}-${service.objekt ?? ''}-${index}`} service={service} palette={palette} />
      ))}
    </Box>
  </Box>
);


// ── Analyse-Kacheln ───────────────────────────────────────────────────────

const StatTile: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: React.ReactNode;
  badge?: React.ReactNode;
  lines?: (string | null | undefined)[];
  aside?: React.ReactNode;
  warn?: boolean;
  testId?: string;
  palette: SwipePalette;
  children?: React.ReactNode;
}> = ({ icon, title, value, badge, lines = [], aside, warn, testId, palette, children }) => (
  <Box
    data-testid={testId}
    sx={{
      p: 1.5,
      borderRadius: 2.5,
      bgcolor: palette.tile,
      border: `1px solid ${warn ? palette.amberBorder : palette.tileBorder}`,
      minWidth: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      {icon}
      <Typography sx={{ fontSize: '0.8rem', color: palette.muted, fontWeight: 600 }}>{title}</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', lineHeight: 1.2, color: palette.text, wordBreak: 'break-word' }}>
            {value}
          </Typography>
          {badge}
        </Box>
        {lines.filter(Boolean).map((line) => (
          <Typography key={String(line)} sx={{ fontSize: '0.72rem', color: palette.muted, lineHeight: 1.45 }}>
            {line}
          </Typography>
        ))}
      </Box>
      {aside}
    </Box>
    {children}
  </Box>
);

const ByteHistogram: React.FC<{
  values: Array<number | null | undefined>;
  current: number | null | undefined;
  color: string;
  palette: SwipePalette;
}> = ({ values, current, color, palette }) => {
  const classes = byteHistogram(values, current);
  const max = Math.max(1, ...classes.map((item) => item.count));
  if (classes.length === 0) return null;
  const currentIndex = classes.findIndex((item) => item.current);
  const barColor = palette.isDark ? 'rgba(255,255,255,0.16)' : 'rgba(15, 23, 42, 0.14)';
  return (
    <Box
      data-testid="firewall-bytes-distribution"
      role="img"
      aria-label={currentIndex >= 0 ? `Verteilung im VDOM, diese Regel bei ${classes[currentIndex].label}` : 'Verteilung im VDOM'}
      sx={{ mt: 1.25 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: 40 }}>
        {classes.map((item) => (
          <Tooltip
            key={item.bucket}
            title={`${item.range}: ${item.count} ${item.count === 1 ? 'Regel' : 'Regeln'}${item.current ? ' · diese Regel' : ''}`}
            arrow
          >
            <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
              <Box
                data-testid={item.current ? 'firewall-bytes-current' : 'firewall-bytes-bar'}
                data-bucket={item.bucket}
                sx={{
                  width: '100%',
                  height: Math.max(item.count > 0 ? 6 : 3, Math.round((item.count / max) * 40)),
                  borderRadius: '3px 3px 1px 1px',
                  bgcolor: item.current ? color : barColor,
                  boxShadow: item.current ? `inset 0 0 0 1px ${color}` : undefined,
                }}
              />
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: '4px', mt: 0.4 }}>
        {classes.map((item) => (
          <Typography
            key={item.label}
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: 'center',
              fontSize: '0.6rem',
              lineHeight: 1.15,
              fontWeight: item.current ? 800 : 500,
              color: item.current ? color : palette.faint,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.label}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: '4px', mt: 0.25, height: 14, position: 'relative' }}>
        {classes.map((item, index) => (
          <Box key={item.bucket} sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            {item.current && (
              <Typography
                data-testid="firewall-bytes-current-label"
                sx={{
                  position: 'absolute',
                  top: 0,
                  fontSize: '0.66rem',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  color,
                  whiteSpace: 'nowrap',
                  ...(classes.length === 1 || (index > 0 && index < classes.length - 1)
                    ? { left: '50%', transform: 'translateX(-50%)' }
                    : index === 0
                      ? { left: 0 }
                      : { right: 0 }),
                }}
              >
                diese Regel
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Peer-Reihe ───────────────────────────────────────────────────────────

export const RatingDot: React.FC<{
  farbe: FirewallFarbe | null;
  label: string;
  tooltip?: string;
  size?: number;
}> = ({ farbe, label, tooltip, size = 28 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const avatar = (
    <Avatar
      data-testid="firewall-rating-dot"
      data-farbe={farbe ?? 'offen'}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        fontWeight: 800,
        bgcolor: farbe ? FARBE_FILL[farbe] : isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
        color: farbe ? FARBE_TEXT[farbe] : 'text.disabled',
        border: farbe ? 'none' : `1px dashed ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)'}`,
      }}
    >
      {label}
    </Avatar>
  );
  return tooltip ? <Tooltip title={tooltip} arrow>{avatar}</Tooltip> : avatar;
};

/** Reihe der Pentester-Bewertungen in der Karte (Seitenpanel auf der Audit-Seite). */
export const PeerRatings: React.FC<{
  rule: FirewallRegel;
  team: FirewallTeamMitglied[];
  showTexts: boolean;
}> = ({ rule, team, showTexts }) => {
  const disagreement = colorsDisagree(rule.bewertungen.map((item) => item.farbe));
  const members = team.length > 0
    ? team
    : rule.bewertungen.map((item) => ({ id: item.mitarbeiter_id, name: item.name, initialen: item.initialen ?? '?' }));
  return (
    <Box data-testid="firewall-peer-ratings" sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
        <Typography variant="subtitle2">Bewertungen</Typography>
        {disagreement && (
          <Chip
            data-testid="firewall-final-disagreement"
            size="small"
            color="warning"
            icon={<WarningAmberIcon />}
            label="Farben weichen voneinander ab"
          />
        )}
        {rule.weicht_vom_final_ab && <Chip size="small" color="warning" label="Weicht vom Final ab" />}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {members.map((member) => {
          const bewertung = rule.bewertungen.find((item) => item.mitarbeiter_id === member.id);
          const farbe = bewertung?.farbe ?? null;
          return (
            <Paper
              key={member.id}
              variant="outlined"
              sx={{
                p: 1,
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                borderRadius: 2,
                minWidth: showTexts ? 220 : undefined,
                flex: showTexts ? '1 1 220px' : undefined,
                borderColor: disagreement && farbe ? FARBE_FILL[farbe] : undefined,
                borderWidth: disagreement && farbe ? 2 : 1,
              }}
            >
              <RatingDot farbe={farbe} label={member.initialen} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{member.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {farbe ? FARBE_LABEL[farbe] : 'offen'}
                </Typography>
                {showTexts && bewertung && (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{bewertung.text || '–'}</Typography>
                )}
              </Box>
            </Paper>
          );
        })}
        {members.length === 0 && (
          <Typography variant="body2" color="text.secondary">Noch keine Einzelbewertungen.</Typography>
        )}
        {rule.konsens && (
          <Paper
            variant="outlined"
            data-testid="firewall-consensus-chip"
            sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'flex-start', borderRadius: 2, borderColor: FARBE_FILL[rule.konsens.farbe], borderWidth: 2 }}
          >
            <RatingDot farbe={rule.konsens.farbe} label="F" tooltip="Finaler Check" />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>Konsens</Typography>
              <Typography variant="caption" color="text.secondary">{FARBE_LABEL[rule.konsens.farbe]}</Typography>
              {showTexts && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{rule.konsens.text || '–'}</Typography>
              )}
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

// ── Ähnliche Regeln ───────────────────────────────────────────────────────

const REASON_LABEL: Record<string, string> = { ziel: 'gleiches Ziel', quelle: 'gleiche Quelle', service: 'gleiche Services' };

const SimilarRules: React.FC<{
  rule: FirewallRegel;
  rules: FirewallRegel[];
  onJump?: (ruleId: number) => void;
  palette: SwipePalette;
}> = ({ rule, rules, onJump, palette }) => {
  const [open, setOpen] = useState(false);
  const similar = useMemo(() => findSimilarRules(rule, rules), [rule, rules]);
  if (similar.length === 0) return null;
  return (
    <Box
      data-testid="firewall-similar-rules"
      sx={{ mt: 1.5, borderRadius: 2.5, bgcolor: palette.tile, border: `1px solid ${palette.tileBorder}`, overflow: 'hidden' }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((value) => !value);
          }
        }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.9, cursor: 'pointer', userSelect: 'none' }}
      >
        <AccountTreeOutlinedIcon sx={{ fontSize: 18, color: palette.green }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: palette.text }}>Ähnliche Regeln</Typography>
        <Chip size="small" label={similar.length} sx={{ height: 20, fontWeight: 700, bgcolor: palette.chipBg, color: palette.text, '& .MuiChip-label': { px: 0.9 } }} />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.78rem', color: palette.muted }}>{open ? 'Details ausblenden' : 'Details anzeigen'}</Typography>
        {open ? <ExpandLessIcon sx={{ fontSize: 18, color: palette.muted }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: palette.muted }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 1.5, pb: 1.5 }}>
          {similar.map(({ rule: other, gruende }) => {
            const own = other.eigene_bewertung?.farbe ?? other.konsens?.farbe ?? null;
            return (
              <Box
                key={other.id}
                data-testid="firewall-similar-rule"
                onClick={() => onJump?.(other.id)}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  border: `1px solid ${palette.tileBorder}`,
                  bgcolor: palette.card,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: onJump ? 'pointer' : 'default',
                  '&:hover': onJump ? { borderColor: palette.green } : undefined,
                }}
              >
                <RatingDot farbe={own} label={own ? '' : '·'} size={14} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: actionColor(other.action), flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: palette.text }}>
                  {other.anzeigename} <Typography component="span" variant="caption" sx={{ color: palette.muted }}>({other.policy_id})</Typography>
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: palette.muted }}>
                  {joinList(other.source)} → {joinList(other.destination)} · {joinList(other.service)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {gruende.map((grund) => (
                    <Chip key={grund} size="small" variant="outlined" label={REASON_LABEL[grund]} sx={{ height: 20, fontSize: '0.65rem', color: palette.muted }} />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Karte ─────────────────────────────────────────────────────────────────

export interface FirewallRuleCardProps {
  rule: FirewallRegel;
  vdomLabel: string;
  /** Name der Firewall; nur gesetzt, wenn das Audit mehrere Firewalls hat. */
  firewallName?: string | null;
  rules: FirewallRegel[];
  statistik: FirewallStatistik;
  vdomStatistik?: FirewallStatistik | null;
  team?: FirewallTeamMitglied[];
  /** Bestimmt den Status-Chip im Kopf: eigene Bewertung (swipe) oder Konsens (final). */
  ratingMode?: 'swipe' | 'final';
  showPeerRatings?: boolean;
  showPeerTexts?: boolean;
  compact?: boolean;
  onJumpToRule?: (ruleId: number) => void;
  sx?: SxProps<Theme>;
  paperProps?: React.HTMLAttributes<HTMLDivElement>;
}

/** „Regel 200“ bei namenlosen Policies, sonst der Name aus dem Export. */
export function ruleTitle(rule: Pick<FirewallRegel, 'anzeigename' | 'policy_id'>): string {
  const name = (rule.anzeigename ?? '').trim();
  if (!name || name === rule.policy_id) return `Regel ${rule.policy_id}`;
  return name;
}

const FirewallRuleCard: React.FC<FirewallRuleCardProps> = ({
  rule,
  vdomLabel,
  firewallName,
  rules,
  statistik,
  vdomStatistik,
  team = [],
  ratingMode = 'swipe',
  showPeerRatings = false,
  showPeerTexts = false,
  compact = false,
  onJumpToRule,
  sx,
  paperProps,
}) => {
  const palette = useSwipePalette();
  const analyse = rule.analyse;
  const policyComment = (rule.firewall_kommentar ?? '').trim();
  const hasPolicyComment = policyComment.length > 0;
  const policyDisabled = (rule.policy_status ?? '').trim().toLowerCase() === 'disable';
  const profiles = rule.security_profiles.filter((item) => String(item).trim().length > 0);
  const color = actionColor(rule.action);
  const inspectionProzent = statistik.accept_ohne_inspection_prozent;
  const vdomInspection = vdomStatistik?.accept_ohne_inspection_prozent;
  const vdomScope = vdomLabel ? `in ${vdomLabel}` : 'im VDOM';
  const ownOpen = showPeerRatings && team.length > 0
    ? team.filter((member) => ratingByMember(rule, member.id) === null).length
    : 0;
  const state = ratingState(rule, ratingMode);
  const stateColor = state === 'offen' ? palette.faint : RATING_UI_COLOR[state];
  const inspectionValue = profiles.length > 0 ? profiles.join(', ') : isAccept(rule.action) ? 'no-inspection' : '–';
  const warnText = palette.isDark ? palette.amber : WARN_TEXT_LIGHT;

  return (
    <Paper
      data-testid="firewall-rule-card"
      elevation={0}
      {...paperProps}
      sx={{
        p: compact ? 2 : 2.5,
        borderRadius: 3,
        bgcolor: palette.card,
        border: `1px solid ${palette.cardBorder}`,
        color: palette.text,
        ...(sx as object),
      }}
    >
      {/* Kopf */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography
          component="h2"
          data-testid="firewall-card-title"
          sx={{ fontWeight: 800, fontSize: compact ? '1.3rem' : '1.65rem', lineHeight: 1.2, color: palette.text, overflowWrap: 'anywhere' }}
        >
          {ruleTitle(rule)}
        </Typography>
        <Typography data-testid="firewall-card-vdom" sx={{ fontSize: '0.78rem', color: palette.muted }}>
          {firewallName ? `${firewallName} › ` : ''}VDOM {vdomLabel} · ID {rule.policy_id}
        </Typography>
        <Chip
          size="small"
          data-testid="firewall-card-state"
          data-state={state}
          icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stateColor, ml: '6px !important' }} />}
          label={ratingStateLabel(state)}
          sx={{ height: 22, fontSize: '0.72rem', bgcolor: palette.chipBg, color: state === 'offen' ? palette.muted : palette.text, fontWeight: 600 }}
        />
        <Chip size="small" variant="outlined" label={rule.abschnitt} data-testid="firewall-card-abschnitt" sx={{ height: 22, fontSize: '0.7rem', color: palette.muted, borderColor: palette.tileBorder }} />
        {analyse.typ_abweichend && <Chip size="small" variant="outlined" label={`Typ ${rule.regel_typ}`} sx={{ height: 22, fontSize: '0.7rem' }} />}
        {rule.inhalt_geaendert && <Chip size="small" color="warning" label="Inhalt geändert" sx={{ height: 22, fontSize: '0.7rem' }} />}
        {rule.entfernt && <Chip size="small" label="Nicht mehr im Import" sx={{ height: 22, fontSize: '0.7rem' }} />}
        {!showPeerRatings && rule.weicht_vom_final_ab && <Chip size="small" color="warning" label="Weicht vom Final ab" sx={{ height: 22, fontSize: '0.7rem' }} />}
      </Box>

      {/* Prüfhinweise: feste Position auf jeder Karte, direkt unter dem Kopf */}
      <RiskStrip risiken={rule.risiken ?? []} compact={compact} />

      {/* Quelle/Ziel und darunter Dienste bzw. Analyse-Kacheln in zwei gleichen Spalten */}
      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mt: 2, mb: 1, color: palette.text }}>Verbindung</Typography>
      <Box
        data-testid="firewall-rule-columns"
        sx={{
          display: 'grid',
          gridTemplateColumns: compact ? '1fr' : { xs: '1fr', sm: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <Box data-testid="firewall-connection" sx={{ display: 'contents' }}>
          <AddressBox
            title="Quelle"
            items={rule.source_aufgeloest}
            side={rule.interface_quelle}
            align="left"
            compact={compact}
            testId="firewall-source-tile"
            palette={palette}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignSelf: 'stretch', py: { xs: 0, sm: 0.5 } }}>
            <ConnectionArrow rule={rule} compact={compact} />
          </Box>
          <AddressBox
            title="Ziel"
            items={rule.destination_aufgeloest}
            side={rule.interface_ziel}
            align="right"
            compact={compact}
            testId="firewall-destination-tile"
            palette={palette}
          />
        </Box>
        <Box data-testid="firewall-analysis" sx={{ display: 'contents' }}>
          <ServicesTable services={rule.services} palette={palette} compact={compact} />
          <Box aria-hidden sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0, width: '100%' }}>
          <StatTile
            testId="firewall-card-comment"
            palette={palette}
            icon={<ChatBubbleOutlineIcon sx={{ fontSize: 18, color: hasPolicyComment ? palette.blue : palette.amber }} />}
            title="Policy-Kommentar"
            value={hasPolicyComment ? policyComment : 'Kein Kommentar'}
            badge={!hasPolicyComment ? (
              <Chip size="small" label="Fehlt" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, color: warnText, border: `1px solid ${WARN_BORDER}`, bgcolor: WARN_BG }} />
            ) : undefined}
            warn={!hasPolicyComment}
          />
          <StatTile
            testId="firewall-tile-policy-status"
            palette={palette}
            icon={<PowerSettingsNewIcon sx={{ fontSize: 18, color: policyDisabled ? palette.amber : palette.muted }} />}
            title="Policy-Status"
            value={policyDisabled ? 'Status deaktiviert' : 'Status aktiviert'}
            warn={policyDisabled}
          />
          <StatTile
            testId="firewall-tile-bytes"
            palette={palette}
            icon={<BarChartIcon sx={{ fontSize: 18, color: palette.blue }} />}
            title="Datenvolumen"
            value={analyse.nie_genutzt ? '0 B' : rule.bytes || '–'}
            badge={analyse.nie_genutzt ? (
              <Chip size="small" label="Nie genutzt" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, color: warnText, border: `1px solid ${WARN_BORDER}`, bgcolor: WARN_BG }} />
            ) : undefined}
            lines={[
              [percentileText(analyse.perzentil_audit, 'im Audit'), percentileText(analyse.perzentil_vdom, vdomScope)]
                .filter(Boolean)
                .join(' · '),
            ]}
            warn={analyse.nie_genutzt}
          >
            <ByteHistogram
              values={rules.map((item) => item.analyse?.bytes_wert ?? item.bytes_wert)}
              current={analyse.bytes_wert ?? (analyse.nie_genutzt ? 0 : null)}
              color={analyse.nie_genutzt ? palette.amber : color}
              palette={palette}
            />
          </StatTile>
          <StatTile
            testId="firewall-tile-inspection"
            palette={palette}
            icon={<ShieldOutlinedIcon sx={{ fontSize: 18, color: analyse.inspection_fehlt ? palette.amber : palette.green }} />}
            title="Inspection"
            value={inspectionValue}
            badge={analyse.inspection_fehlt ? (
              <Chip size="small" label="Ohne Prüfung" data-testid="firewall-inspection-missing" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, color: warnText, border: `1px solid ${WARN_BORDER}`, bgcolor: WARN_BG }} />
            ) : undefined}
            lines={inspectionProzent != null
              ? [
                `${inspectionProzent} % der ACCEPT-Regeln im Audit ohne Inspection`,
                vdomInspection != null ? `${vdomInspection} % ${vdomScope}` : null,
              ]
              : []}
            warn={analyse.inspection_fehlt}
          />
          <StatTile
            testId="firewall-tile-log"
            palette={palette}
            icon={<ArticleOutlinedIcon sx={{ fontSize: 18, color: analyse.log_abweichend ? palette.amber : palette.blue }} />}
            title="Logging"
            value={rule.log || '–'}
            badge={analyse.log_abweichend ? (
              <Chip size="small" label="Abweichend" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, color: warnText, border: `1px solid ${WARN_BORDER}`, bgcolor: WARN_BG }} />
            ) : undefined}
            lines={statistik.log_nicht_all_prozent != null ? [`${statistik.log_nicht_all_prozent} % im Audit nicht „All“`] : []}
            warn={analyse.log_abweichend}
          />
          {analyse.schedule_abweichend && (
            <StatTile
              testId="firewall-tile-schedule"
              palette={palette}
              icon={<ScheduleIcon sx={{ fontSize: 18, color: palette.amber }} />}
              title="Zeitplan"
              value={joinList(rule.schedule)}
              lines={[`${statistik.schedule_abweichend} Regel${statistik.schedule_abweichend === 1 ? '' : 'n'} im Audit mit Zeitplan`]}
              warn
            />
          )}
          </Box>
        </Box>
      </Box>

      {showPeerRatings && (
        <PeerRatings rule={rule} team={team} showTexts={showPeerTexts} />
      )}
      {showPeerRatings && ownOpen > 0 && (
        <Alert severity="info" sx={{ mt: 1, py: 0 }}>
          {ownOpen} Pentester {ownOpen === 1 ? 'hat' : 'haben'} diese Regel noch nicht bewertet.
        </Alert>
      )}

      <SimilarRules rule={rule} rules={rules} onJump={onJumpToRule} palette={palette} />
    </Paper>
  );
};

export default FirewallRuleCard;
